import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Paper from "@mui/material/Paper";
import {
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { ContentCopy, Check, Assessment, ViewColumn } from "@mui/icons-material";
import AddFeesDialog from "./AddFeesDialog";
import SnackbarComponent from "../Common/SnackbarComponent";
import EditStudent from "./EditStudent";
import Loader from "../Common/Loader";
import StudentReportModal from "./StudentReportModal";
import api from "../Auth/Middleware";

const today = new Date().toISOString().split("T")[0];

export default function StudentTable(props) {
  const batch = props.batch;
  const role = sessionStorage.getItem("role");
  const isAdmin = role === "Admin";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [snackbarMsg, setSnackbarMSg] = useState("");
  const [snackbarSevierty, setSnackbarSevierty] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [studentData, setStudentData] = useState(null);

  // Status filter
  const [statusFilter, setStatusFilter] = useState("All");

  // Column visibility
  const toggleableColumns = [
    { field: "name", label: "Name" },
    { field: "dob", label: "Date of Birth" },
    { field: "class", label: "Class" },
    { field: "fathername", label: "Father's Name" },
    { field: "contact", label: "Contact" },
    { field: "status", label: "Status" },
    { field: "pendingAmount", label: "Fees Status" },
  ];
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [colMenuAnchor, setColMenuAnchor] = useState(null);

  const toggleCol = (field) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      next.has(field) ? next.delete(field) : next.add(field);
      return next;
    });
  };

  // Withdrawal state
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawStudent, setWithdrawStudent] = useState(null);
  const [withdrawDate, setWithdrawDate] = useState(today);

  // Reset password state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetStudent, setResetStudent] = useState(null);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [resetCredentials, setResetCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Report state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportStudentId, setReportStudentId] = useState(null);

  const columnDefs = [
    { headerName: "Name", field: "name", sortable: true, filter: true, hide: hiddenCols.has("name") },
    { headerName: "Date of Birth", field: "dob", sortable: true, filter: true, hide: hiddenCols.has("dob") },
    { headerName: "Class", field: "class", sortable: true, filter: true, hide: hiddenCols.has("class") },
    { headerName: "Father's Name", field: "fathername", sortable: true, filter: true, hide: hiddenCols.has("fathername") },
    { headerName: "Contact", field: "contact", sortable: false, filter: true, hide: hiddenCols.has("contact") },
    {
      headerName: "Status",
      field: "status",
      sortable: true,
      filter: true,
      hide: hiddenCols.has("status"),
      cellRenderer: (params) => {
        if (params.value === "Withdrawn")
          return <Chip label="Withdrawn" size="small" sx={{ bgcolor: "#9e9e9e", color: "#fff" }} />;
        if (params.value === "Completed")
          return <Chip label="Completed" size="small" color="primary" />;
        return <Chip label="Active" size="small" color="success" />;
      },
    },
    {
      headerName: "Actions",
      minWidth: 400,
      cellRenderer: (params) => {
        const isActive = params.data.status === "Active";
        return (
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => handleEdit(params.data.id)}
              variant="outlined"
              color="primary"
              size="small"
            >
              Edit
            </Button>
            <Button
              onClick={() => handleAddFeesClick(params.data.id)}
              variant="outlined"
              color="secondary"
              size="small"
            >
              Add Fees
            </Button>
            {isAdmin && isActive && (
              <Button
                onClick={() =>
                  handleWithdrawClick({
                    id: params.data.id,
                    name: params.data.name,
                    batchEnrollmentId: params.data.batchEnrollmentId,
                  })
                }
                variant="outlined"
                color="error"
                size="small"
              >
                Withdraw
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() =>
                  handleResetPasswordClick({
                    id: params.data.id,
                    name: params.data.name,
                    firstName: params.data.firstName,
                    dobYear: params.data.dobYear,
                  })
                }
                variant="outlined"
                color="warning"
                size="small"
              >
                Reset Password
              </Button>
            )}
            {isAdmin && (
              <Tooltip title="Generate Report">
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => {
                    setReportStudentId(params.data.id);
                    setReportDialogOpen(true);
                  }}
                >
                  <Assessment fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      headerName: "Fees Status",
      field: "pendingAmount",
      sortable: true,
      filter: true,
      hide: hiddenCols.has("pendingAmount"),
      cellStyle: (params) => {
        const value = params.value;
        if (value === "Please add fees") return { color: "purple", fontWeight: "bold" };
        if (value === "0") return { color: "green", fontWeight: "bold" };
        return { color: "red", fontWeight: "bold" };
      },
    },
  ];

  useEffect(() => {
    const fetchAllFeesData = async () => {
      try {
        const transformedArray = props?.data.map((obj) => ({
          id: obj.id,
          name: obj.firstName + " " + obj.lastName,
          firstName: obj.firstName,
          dobYear: new Date(obj.dateOfBirth).getFullYear(),
          dob: new Date(obj.dateOfBirth).toLocaleDateString("en-GB"),
          class: `${obj.class}th`,
          fathername: obj.fatherName,
          contact: obj.parentContact,
          pendingAmount: null,
          batchEnrollmentId: obj.batchEnrollmentId,
          status: obj.enrollmentStatus || "Active",
        }));

        const updatedData = await Promise.all(
          transformedArray.map(async (student) => {
            try {
              const response = await api.get(`Student/Fees/${student.id}`);
              const feesData = response.data;
              if (feesData?.length > 0) {
                const totalFees = feesData[0]?.total || 0;
                const paidSum = feesData.reduce((acc, fee) => acc + fee.amoutPaid, 0);
                const pendingAmount = totalFees - paidSum;
                return {
                  ...student,
                  pendingAmount: totalFees === 0 ? "Please add fees" : `${pendingAmount}`,
                };
              } else {
                return { ...student, pendingAmount: "Please add fees" };
              }
            } catch {
              return { ...student, totalFees: "Error", pendingAmount: "Error" };
            }
          })
        );

        setStudentData(updatedData);
      } catch (error) {
        console.error("Error fetching fees data:", error);
      }
    };

    fetchAllFeesData();
  }, [props.data]);

  const handleAddFeesClick = (id) => {
    setEditDialogOpen(false);
    setDialogOpen(true);
    setSelectedRowId(id);
  };

  const handleEdit = (id) => {
    setDialogOpen(false);
    setEditDialogOpen(true);
    setSelectedRowId(id);
  };

  const handleWithdrawClick = (student) => {
    setWithdrawStudent(student);
    setWithdrawDate(today);
    setWithdrawDialogOpen(true);
  };

  const handleWithdrawConfirm = async () => {
    try {
      await api.patch(
        `Student/WithdrawStudent/${withdrawStudent.batchEnrollmentId}?withdrawalDate=${withdrawDate}`
      );
      setStudentData((prev) =>
        prev.map((s) =>
          s.batchEnrollmentId === withdrawStudent.batchEnrollmentId
            ? { ...s, status: "Withdrawn" }
            : s
        )
      );
      setSnackbarOpen(true);
      setSnackbarMSg(`${withdrawStudent.name} marked as withdrawn.`);
      setSnackbarSevierty("success");
      setWithdrawDialogOpen(false);
      setWithdrawStudent(null);
      if (props.onWithdraw) props.onWithdraw();
    } catch (error) {
      setSnackbarOpen(true);
      setSnackbarMSg("Failed to withdraw student. Please try again.");
      setSnackbarSevierty("error");
    }
  };

  const handleResetPasswordClick = (student) => {
    setResetStudent(student);
    setResetDialogOpen(true);
  };

  const handleResetPasswordConfirm = async () => {
    try {
      const response = await api.post(`Student/ResetPassword/${resetStudent.id}`);
      setResetCredentials(response.data);
      setResetDialogOpen(false);
      setCopiedField(null);
      setCredentialsDialogOpen(true);
    } catch (error) {
      setSnackbarOpen(true);
      setSnackbarMSg("Failed to reset password. Please try again.");
      setSnackbarSevierty("error");
      setResetDialogOpen(false);
    }
  };

  const handleCopy = (field, value) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleCloseDialog = () => {
    setSelectedRowId(null);
    setDialogOpen(false);
  };

  const handleEditCloseDialog = () => {
    setSelectedRowId(null);
    setEditDialogOpen(false);
  };

  const openSuccessBar = () => {
    setSnackbarOpen(true);
    setSnackbarMSg("Fees added successfully!!");
    setSnackbarSevierty("success");
  };

  const openFailBar = () => {
    setSnackbarOpen(true);
    setSnackbarMSg("Couldn't save the changes, Please try again");
    setSnackbarSevierty("error");
  };

  const openEditSuccessBar = () => {
    setSnackbarOpen(true);
    setSnackbarMSg("Student Edited successfully!!");
    setSnackbarSevierty("success");
  };

  const closeSnackbar = () => {
    setSnackbarOpen(false);
  };

  const getStudentDataByID = (id) => {
    return props?.data.find((student) => student.id === id);
  };

  const selectedRow = studentData?.find((row) => row.id === selectedRowId);

  const filteredStudentData =
    statusFilter === "All"
      ? studentData
      : studentData?.filter((s) => s.status === statusFilter);

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        severity={snackbarSevierty}
        message={snackbarMsg}
        onClose={closeSnackbar}
      />
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        {!studentData ? (
          <Loader />
        ) : (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              {isAdmin ? (
                <ToggleButtonGroup
                  value={statusFilter}
                  exclusive
                  onChange={(_, val) => val && setStatusFilter(val)}
                  size="small"
                >
                  <ToggleButton value="All">All</ToggleButton>
                  <ToggleButton value="Active">Active</ToggleButton>
                  <ToggleButton value="Withdrawn">Withdrawn</ToggleButton>
                  <ToggleButton value="Completed">Completed</ToggleButton>
                </ToggleButtonGroup>
              ) : <span />}
              <Tooltip title="Manage columns">
                <IconButton size="small" onClick={(e) => setColMenuAnchor(e.currentTarget)}>
                  <ViewColumn fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Menu
              anchorEl={colMenuAnchor}
              open={Boolean(colMenuAnchor)}
              onClose={() => setColMenuAnchor(null)}
              PaperProps={{ sx: { minWidth: 200 } }}
            >
              {toggleableColumns.map(({ field, label }) => (
                <MenuItem key={field} dense onClick={() => toggleCol(field)}>
                  <Checkbox checked={!hiddenCols.has(field)} size="small" sx={{ py: 0 }} />
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </Menu>
          <div className="ag-theme-alpine" style={{ width: "100%" }}>
            <AgGridReact
              columnDefs={columnDefs}
              rowData={filteredStudentData}
              pagination={true}
              paginationPageSize={20}
              domLayout="autoHeight"
              defaultColDef={{ resizable: true }}
              animateRows={true}
              getRowStyle={(params) =>
                params.data.status === "Withdrawn"
                  ? { opacity: 0.55, background: "#f5f5f5" }
                  : null
              }
            />
          </div>
          </>
        )}
      </Paper>

      {/* Withdrawal confirmation dialog */}
      <Dialog
        open={withdrawDialogOpen}
        onClose={() => setWithdrawDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Withdrawal</DialogTitle>
        <DialogContent>
          <p>
            Confirm withdrawal date for <strong>{withdrawStudent?.name}</strong>:
          </p>
          <TextField
            label="Withdrawal Date"
            type="date"
            fullWidth
            value={withdrawDate}
            onChange={(e) => setWithdrawDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: today }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleWithdrawConfirm} variant="contained" color="error">
            Mark as Withdrawn
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset password confirmation dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography>
            Reset password for <strong>{resetStudent?.name}</strong>?
          </Typography>
          <Typography sx={{ mt: 1, color: "text.secondary", fontSize: "0.9rem" }}>
            Their new password will be{" "}
            <strong>
              {resetStudent?.firstName?.toLowerCase()}@{resetStudent?.dobYear}
            </strong>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetPasswordConfirm} variant="contained" color="warning">
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credentials display dialog */}
      <Dialog
        open={credentialsDialogOpen}
        onClose={() => setCredentialsDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Password Reset Successfully</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Share these credentials with the student.
          </Typography>

          {/* Username row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ bgcolor: "#f5f5f5", borderRadius: 1, px: 2, py: 1, mb: 1 }}
          >
            <div>
              <Typography variant="caption" color="text.secondary">Username</Typography>
              <Typography variant="body1" fontWeight={600}>
                {resetCredentials?.username}
              </Typography>
            </div>
            <Tooltip title={copiedField === "username" ? "Copied!" : "Copy"}>
              <IconButton
                size="small"
                onClick={() => handleCopy("username", resetCredentials?.username)}
              >
                {copiedField === "username" ? (
                  <Check fontSize="small" color="success" />
                ) : (
                  <ContentCopy fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Password row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ bgcolor: "#f5f5f5", borderRadius: 1, px: 2, py: 1 }}
          >
            <div>
              <Typography variant="caption" color="text.secondary">New Password</Typography>
              <Typography variant="body1" fontWeight={600}>
                {resetCredentials?.password}
              </Typography>
            </div>
            <Tooltip title={copiedField === "password" ? "Copied!" : "Copy"}>
              <IconButton
                size="small"
                onClick={() => handleCopy("password", resetCredentials?.password)}
              >
                {copiedField === "password" ? (
                  <Check fontSize="small" color="success" />
                ) : (
                  <ContentCopy fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredentialsDialogOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {selectedRow && selectedRow.name && (
        <AddFeesDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onSuccess={openSuccessBar}
          onFail={openFailBar}
          studentDetail={selectedRow}
          id={selectedRowId}
          batchEnrollmentId={selectedRow?.batchEnrollmentId}
          batch={batch}
        />
      )}
      {selectedRow && (
        <EditStudent
          openEdit={editDialogOpen}
          onEditClose={handleEditCloseDialog}
          onEditSuccess={openEditSuccessBar}
          onEditFail={openFailBar}
          studentDetail={getStudentDataByID(selectedRowId)}
          id={selectedRowId}
          batch={batch}
        />
      )}
      <StudentReportModal
        open={reportDialogOpen}
        onClose={() => { setReportDialogOpen(false); setReportStudentId(null); }}
        studentId={reportStudentId}
        batch={batch}
      />
    </>
  );
}
